import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const LanguageContext = createContext();

export const useLanguageData = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguageData must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [languages, setLanguages] = useState({});
  const [proficiencyLevels, setProficiencyLevels] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const languagesRes = await axios.get('/lb-api/languages', { withCredentials: true });
        setLanguages(languagesRes.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching language data:', err);
        setError(err);
        
        // Fallback to hardcoded data
        setLanguages({
          en: { label: 'English', flag: '/images/flag-english.svg', active: true },
          fr: { label: 'French', flag: '/images/flag-french.svg', active: true },
          de: { label: 'German', flag: '/images/flag-german.svg', active: true },
          es: { label: 'Spanish', flag: '/images/flag-spanish.svg', active: true },
          it: { label: 'Italian', flag: '/images/flag-italian.svg', active: true },
          ja: { label: 'Japanese', flag: '/images/flag-japanese.svg', active: true },
        });
        setLoading(false);
      }
    };

    fetchLanguages();
  }, []);

  // Function to fetch levels for a specific language
  const fetchLevelsForLanguage = useCallback(async (languageCode) => {
    // Clear existing levels first to prevent showing stale data
    setProficiencyLevels({});
    
    try {
      const levelsRes = await axios.get(`/lb-api/proficiency-levels/${languageCode}`, { 
        withCredentials: true 
      });
      setProficiencyLevels(levelsRes.data);
      return levelsRes.data;
    } catch (err) {
      console.error('Error fetching proficiency levels:', err);
      
      // Fallback to hardcoded data
      const isJapanese = languageCode === 'ja';
      const fallbackLevels = isJapanese
        ? {
            'beginner-n5': { label: 'N5 - Beginner', active: true },
            'elementary-n4': { label: 'N4 - Elementary', active: true },
            'pre-intermediate-n3': { label: 'N3 - Pre-Intermediate', active: true },
            'intermediate-n2': { label: 'N2 - Intermediate', active: true },
            'advanced-n1': { label: 'N1 - Advanced', active: true },
          }
        : {
            'beginner-a1': { label: 'Beginner A1', active: true },
            'elementary-a2': { label: 'Elementary A2', active: true },
            'pre-intermediate-b1': { label: 'Pre-Intermediate B1', active: true },
            'intermediate-b2': { label: 'Intermediate B2', active: true },
            'advanced-c1': { label: 'Advanced C1', active: true },
            'proficient-c2': { label: 'Proficient C2', active: true },
          };
      setProficiencyLevels(fallbackLevels);
      return fallbackLevels;
    }
  }, []);

  const value = {
    languages,
    proficiencyLevels,
    loading,
    error,
    fetchLevelsForLanguage
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
